import { authAPI, adminAPI } from './api';
import { clearAuthScope, setAuthSession, type AuthScope } from '../utils/authStorage';

export interface User {
  id: string;
  email: string;
  name?: string;
  avatar_url?: string;
  role?: string;
  is_active?: boolean;
  mustChangePassword?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface AuthResponse {
  user: User | null;
  error: string | null;
  token?: string;
  mustChangePassword?: boolean;
}

// 注册（成功后不自动登录，由页面跳转登录）
export async function signUp(email: string, password: string, name: string): Promise<AuthResponse> {
  try {
    const response = await authAPI.register(email, password, name);
    return {
      user: response.user,
      error: null,
      token: response.token,
      mustChangePassword: false,
    };
  } catch (error: any) {
    console.error('Sign up catch error:', error);
    return { user: null, error: error.message || '注册失败' };
  }
}

// 登录：按角色写入对应作用域
export async function signIn(email: string, password: string): Promise<AuthResponse> {
  try {
    const response = await authAPI.login(email, password);
    const mustChangePassword = !!response.mustChangePassword;
    const scope: AuthScope = response.user?.role === 'admin' ? 'admin' : 'frontend';
    if (response.token) {
      setAuthSession(scope, response.token, response.user, { mustChangePassword });
    }
    return {
      user: { ...response.user, mustChangePassword },
      error: null,
      token: response.token,
      mustChangePassword,
    };
  } catch (error: any) {
    console.error('登录错误:', error);
    return { user: null, error: error.message || '登录失败，请重试' };
  }
}

// 登出：只清当前作用域，禁止 clear()
export async function signOut(): Promise<{ error: string | null }> {
  try {
    clearAuthScope();
    return { error: null };
  } catch (error: any) {
    console.error('Sign out error:', error);
    return { error: error.message || '登出失败' };
  }
}

// 获取当前用户
export async function getCurrentUser(token?: string | null): Promise<User | null> {
  try {
    const user = await authAPI.getCurrentUser();
    return user;
  } catch (error: any) {
    console.error('Error in getCurrentUser:', error);
    return null;
  }
}

// 更新用户信息
export async function updateUserProfile(userId: string, updates: Partial<User>): Promise<{ error: string | null }> {
  try {
    const payload: { name?: string; role?: string; is_active?: boolean; email?: string } = {
      name: updates.name,
      role: updates.role,
      is_active: updates.is_active,
    };
    if (updates.email !== undefined) {
      payload.email = updates.email;
    }
    const response = await adminAPI.updateUser(userId, payload);
    if (!response?.success) {
      return { error: '更新失败' };
    }
    return { error: null };
  } catch (error: any) {
    return { error: error.message || '更新失败' };
  }
}

// 创建管理员账号
export async function createAdminAccount(): Promise<{ error: string | null }> {
  try {
    console.log('开始创建管理员账号...');
    // 管理员账号已经在数据库初始化时创建
    return { error: null };
  } catch (error: any) {
    console.error('创建管理员账号失败:', error);
    return { error: error.message || '创建管理员账号失败' };
  }
}

// 获取所有用户列表
export async function getAllUsers(): Promise<{ users: User[]; error: string | null }> {
  try {
    const users = await adminAPI.getAllUsers();
    return { users: users || [], error: null };
  } catch (error: any) {
    return { users: [], error: error.message || '获取用户列表失败' };
  }
}

// 禁止/启用用户
export async function toggleUserStatus(userId: string, isActive: boolean): Promise<{ error: string | null }> {
  try {
    const response = await adminAPI.toggleUserStatus(userId, isActive);
    return { error: response.success ? null : '更新用户状态失败' };
  } catch (error: any) {
    return { error: error.message || '更新用户状态失败' };
  }
}

// 重置用户密码
export async function resetUserPassword(userId: string, newPassword?: string): Promise<{ error: string | null; password?: string }> {
  try {
    const response = await adminAPI.resetUserPassword(userId, newPassword);
    return { error: response.success ? null : '重置密码失败', password: newPassword };
  } catch (error: any) {
    return { error: error.message || '重置密码失败', password: undefined };
  }
}

// 删除用户
export async function deleteUser(userId: string): Promise<{ error: string | null }> {
  try {
    const response = await adminAPI.deleteUser(userId);
    return { error: response.success ? null : '删除用户失败' };
  } catch (error: any) {
    return { error: error.message || '删除用户失败' };
  }
}

// 设置用户积分
export async function setUserCredits(userId: string, credits: number): Promise<{ error: string | null }> {
  try {
    const response = await adminAPI.setUserCredits(userId, credits);
    return { error: response.success ? null : '设置积分失败' };
  } catch (error: any) {
    return { error: error.message || '设置积分失败' };
  }
}

// 检查是否为管理员
export function isAdmin(user: User | null): boolean {
  return user?.role === 'admin';
}
